import {
    Box,
    Grid,
    Typography,
    styled,
    useTheme,
    Skeleton,
} from '@mui/material';
import React from 'react';
import { FreeHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/types';
import {
    getFreeHypothesisLabel,
    getHypothesisDetail,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/dataHelpers';
import { formatAmountsNumber } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/dataHelpers';
import { capitalize } from 'helpers/helpers';
import Flex from 'ui/Flex/Flex';
import { useTranslation } from 'react-i18next';

const StyBlock = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: `${theme.palette.lightGrey}`,
    justifyContent: 'space-between',
    padding: '1rem',
    gap: '20px',
    height: '100%',
    borderRadius: '6%',
    width: '100%',
}));
const StyBox = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    padding: '2rem 0',
}));
type Props = {
    freeHypothesisData: FreeHypothesis[];
    selectedHypothesis?: FreeHypothesis | null;
    setSelectedHypothesis?: (hyp: FreeHypothesis) => void;
    editMode?: boolean;
    freeHypothesisTotals: Record<string, number>;
    isLoading?: boolean;
};

const FreeHypothesesListView = ({
    freeHypothesisData,
    selectedHypothesis = null,
    setSelectedHypothesis = () => {},
    editMode = true,
    freeHypothesisTotals,
    isLoading = false,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    const handleClick = (item: FreeHypothesis) => {
        if (setSelectedHypothesis) {
            setSelectedHypothesis(item);
        }
    };

    return (
        <StyBox>
            <Grid container spacing={2}>
                {freeHypothesisData.map((item) => {
                    const hypothesisTotal = freeHypothesisTotals[item.id];

                    return (
                        <Grid item xs={3} key={item.id}>
                            <StyBlock
                                onClick={() => {
                                    if (editMode) {
                                        handleClick(item);
                                    }
                                }}
                                sx={{
                                    backgroundColor: `${
                                        item === selectedHypothesis
                                            ? theme.palette.primary.background
                                            : theme.palette.lightGrey
                                    }`,
                                }}
                            >
                                <Typography
                                    variant="h4"
                                    sx={{
                                        display: 'inline',
                                        overflow: 'hidden',
                                        fontSize: '1rem',
                                    }}
                                >
                                    {getFreeHypothesisLabel(item.filters)}
                                </Typography>
                                {item.filters.category &&
                                    !getFreeHypothesisLabel(
                                        item.filters,
                                    ).includes('Catégorie') && (
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                display: 'inline',
                                                overflow: 'hidden',
                                                fontSize: '1rem',
                                            }}
                                        >
                                            {t(
                                                'humanResources.simulation.freeHypothesis.ifse.category',
                                            )}{' '}
                                            {capitalize(item.filters.category)}
                                        </Typography>
                                    )}
                                {item.filters.cadreEmploi &&
                                item.filters.cadreEmploi !==
                                    getFreeHypothesisLabel(item.filters) ? (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            display: 'inline',
                                            overflow: 'hidden',
                                            fontSize: '1rem',
                                        }}
                                    >
                                        {item.filters.cadreEmploi}
                                    </Typography>
                                ) : (
                                    <>
                                        {item.filters.filiere &&
                                            item.filters.filiere !==
                                                getFreeHypothesisLabel(
                                                    item.filters,
                                                ) && (
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        display: 'inline',
                                                        overflow: 'hidden',
                                                        fontSize: '1rem',
                                                    }}
                                                >
                                                    {item.filters.filiere}
                                                </Typography>
                                            )}
                                    </>
                                )}
                                {item.filters.date && (
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: theme.palette.darkGrey,
                                            maxWidth: '85%',
                                        }}
                                    >
                                        {getHypothesisDetail(item)}
                                    </Typography>
                                )}
                                <Flex direction="column">
                                    <Typography
                                        variant="body1"
                                        sx={{ top: '0' }}
                                    >
                                        {t(
                                            'humanResources.simulation.freeHypothesis.ifse.totalValuePerYear',
                                        )}
                                    </Typography>
                                    <Typography variant="h5">
                                        {isLoading ? (
                                            <Skeleton width="100%" />
                                        ) : (
                                            formatAmountsNumber(
                                                hypothesisTotal ?? 0,
                                            )
                                        )}
                                    </Typography>
                                </Flex>
                            </StyBlock>
                        </Grid>
                    );
                })}
            </Grid>
        </StyBox>
    );
};

export default FreeHypothesesListView;
